import Phaser from "phaser";
import type { LaneStateSummary } from "../../../shared/types/BattleEvent";
import mapConfig from "../game/data/mapConfig.json";
import type { Language } from "../i18n";

type MiniMapViewport = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type MiniMapOptions = {
  language: Language;
  onFocus: (x: number, y: number) => void;
  uiScale?: number;
  highContrastText?: boolean;
};

const WIDTH = 184;
const HEIGHT = 92;
const LEFT = 1082;
const TOP = 68;

export class MiniMapPanel {
  private readonly background: Phaser.GameObjects.Rectangle;
  private readonly graphics: Phaser.GameObjects.Graphics;
  private readonly title: Phaser.GameObjects.Text;
  private states: LaneStateSummary[] = [];
  private viewport: MiniMapViewport = { x: 0, y: 0, width: mapConfig.designWidth, height: mapConfig.designHeight };

  constructor(private readonly scene: Phaser.Scene, private readonly options: MiniMapOptions) {
    this.background = scene.add
      .rectangle(LEFT, TOP, WIDTH, HEIGHT, 0x061018, 0.72)
      .setOrigin(0, 0)
      .setDepth(135)
      .setScrollFactor(0)
      .setStrokeStyle(1, 0x8de7ff, 0.58);
    this.graphics = scene.add.graphics().setDepth(136).setScrollFactor(0);
    this.title = scene.add
      .text(LEFT + 8, TOP + 5, options.language === "ko" ? "전장" : "Map", {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: this.fontSize(11),
        color: "#f7e9bb",
        stroke: "#08070a",
        strokeThickness: this.stroke(1)
      })
      .setResolution(2)
      .setDepth(137)
      .setScrollFactor(0);

    this.background.setInteractive({ useHandCursor: true });
    this.background.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      const localX = Phaser.Math.Clamp(pointer.x - LEFT, 0, WIDTH);
      const localY = Phaser.Math.Clamp(pointer.y - TOP, 0, HEIGHT);
      this.options.onFocus((localX / WIDTH) * mapConfig.designWidth, (localY / HEIGHT) * mapConfig.designHeight);
    });
    this.render();
  }

  update(states: LaneStateSummary[], viewport: MiniMapViewport) {
    this.states = states;
    this.viewport = viewport;
    this.render();
  }

  private render() {
    this.graphics.clear();
    this.graphics.fillStyle(0x10202a, 0.55);
    this.graphics.fillRect(LEFT + 5, TOP + 19, WIDTH - 10, HEIGHT - 25);

    for (const lane of mapConfig.lanes) {
      const y = this.mapY(lane.y);
      const state = this.states.find((candidate) => candidate.lane === lane.lane);
      const color = this.riskColor(state?.riskLevel);
      this.graphics.lineStyle(2, color, 0.58);
      this.graphics.lineBetween(LEFT + 10, y, LEFT + WIDTH - 10, y);

      const playerUnits = state?.playerUnitCount ?? 0;
      const enemyUnits = state?.enemyUnitCount ?? 0;
      if (playerUnits > 0) {
        this.graphics.fillStyle(0x70d7ff, 0.88);
        this.graphics.fillCircle(LEFT + WIDTH - 20, y, Math.min(7, 2 + playerUnits));
      }
      if (enemyUnits > 0) {
        this.graphics.fillStyle(0xff604f, 0.88);
        this.graphics.fillCircle(LEFT + 20, y, Math.min(7, 2 + enemyUnits));
      }

      if (state?.economyOutpostOwner && state.economyOutpostOwner !== "neutral") {
        this.graphics.fillStyle(state.economyOutpostOwner === "player" ? 0x70d7ff : 0xff604f, 0.9);
        this.graphics.fillCircle(LEFT + WIDTH / 2, y, 3.5);
      }
    }

    this.graphics.fillStyle(0x70d7ff, 0.18);
    this.graphics.fillRect(this.mapX(mapConfig.playerCastleX) - 3, TOP + 20, 5, HEIGHT - 26);
    this.graphics.fillStyle(0xff604f, 0.18);
    this.graphics.fillRect(this.mapX(mapConfig.aiFortressX) - 2, TOP + 20, 5, HEIGHT - 26);

    this.graphics.lineStyle(1, 0xf7e9bb, 0.9);
    this.graphics.strokeRect(this.mapX(this.viewport.x), this.mapY(this.viewport.y), Math.max(16, this.scaleX(this.viewport.width)), Math.max(12, this.scaleY(this.viewport.height)));
  }

  private riskColor(level: LaneStateSummary["riskLevel"]) {
    switch (level) {
      case "collapse":
        return 0xff604f;
      case "danger":
        return 0xffb15a;
      case "contested":
        return 0xf3d46e;
      case "watch":
        return 0x83ecff;
      case "safe":
      default:
        return 0x6fe086;
    }
  }

  private mapX(x: number) {
    return LEFT + this.scaleX(x);
  }

  private mapY(y: number) {
    return TOP + this.scaleY(y);
  }

  private scaleX(value: number) {
    return (value / mapConfig.designWidth) * WIDTH;
  }

  private scaleY(value: number) {
    return (value / mapConfig.designHeight) * HEIGHT;
  }

  private fontSize(base: number) {
    return `${Math.round(base * (this.options.uiScale ?? 1))}px`;
  }

  private stroke(base: number) {
    return this.options.highContrastText ? Math.max(2, base + 1) : base;
  }
}
